<template>
  <div
    :class="[
      $style.base,
      {
        [$style.breakLines]: breakLines,
        [$style.selected]: selected
      }
    ]"
    v-test="`item-content-${data.value}`"
  >
    <div
      v-if="resource || data.icon || data.color"
      :class="$style.beforeText"
    >
      <BaseAvatar
        v-if="resource"
        :key="data.value"
        :resource="resource"
        size="s"
        v-test="'_base-dropdown-avatar'"
      />
      <BaseIcon
        v-else-if="data.icon"
        :name="data.icon"
        :size="small ? 's' : 'm'"
        v-test="'_base-dropdown-icon'"
      />
      <BaseColorLabel
        v-else-if="data.color"
        :modelValue="data.color"
        small
        v-test="'_base-dropdown-color'"
      />
    </div>
    <BaseAvatar
      v-if="data.avatar"
      :picture="data.avatar.picture"
      :size="data.avatar.size"
      :name="data.avatar.name"
      :color="data.avatar.color"
      v-test="'_base-dropdown-picture-avatar'"
    />
    <div
      v-if="!hideText"
      :class="[$style.label, { [$style.hasBadgeLabel]: showBadgeLabel }]"
      v-test="'_base-dropdown-label'"
    >
      <template v-if="highlightedLabel.some((part) => part.highlight)">
        <template
          v-for="(part, index) in highlightedLabel"
          :key="index"
        >
          <span
            v-if="part.highlight"
            :class="$style.highlight"
            v-test="'_base-dropdown-highlight'"
            >{{ part.text }}</span
          >
          <template v-else>{{ part.text }}</template>
        </template>
      </template>
      <template v-else>{{ data.label }}</template>
    </div>
    <div
      v-if="showBadgeLabel"
      :class="$style.badgeLabel"
      v-test="'_base-dropdown-badge-label'"
    >
      <BaseLabel color="primary">{{ data.badgeLabel }}</BaseLabel>
    </div>
    <div
      v-if="!selected && data.labelExtra"
      :class="$style.labelExtra"
    >
      {{ data.labelExtra }}
    </div>
    <div
      v-if="warnings.length"
      :class="$style.warnings"
    >
      <BaseIcon
        v-for="(warning, index) in warnings"
        :key="`warning-${index}`"
        :name="warning.icon"
        :tooltip="warning.tooltip"
        :color="warning.color"
        size="s"
        v-test="`_base-dropdown-warning-${warning.icon}`"
      />
    </div>
    <div
      v-if="!selected && data.actions && data.actions.length"
      :class="$style.actions"
    >
      <div
        v-for="(action, index) in data.actions"
        :key="`action-${index}`"
        @click.stop="$emit('action', action)"
        v-test="`_base-dropdown-item-${action}`"
      >
        <BaseIcon :name="action" />
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { computed } from 'vue';
import { useResourcesStore } from '@/stores/resources';
import type { IconName } from '../base-icon/types';

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<{
  data: any;
  showResource?: boolean;
  breakLines?: boolean;
  selected?: boolean;
  hideText?: boolean;
  warning?: {
    icon: IconName;
    tooltip: string;
    color: string;
  };
  searchQuery?: string;
  small?: boolean;
}>();

defineEmits(['action']);

const { resourceById } = useResourcesStore();

const warnings = computed(() =>
  props.warning ? [props.warning] : props.data.warnings || [],
);

const showBadgeLabel = computed(
  () => !props.selected && !!props.data.badgeLabel,
);

const resource = computed(() => {
  if (props.showResource && props.data.value) {
    return resourceById(props.data.value);
  } else {
    return null;
  }
});

const highlightedLabel = computed(() => {
  const label = props.data.label;
  const query = props.searchQuery?.trim();

  if (!query || !label) {
    return [{ text: label, highlight: false }];
  }

  const parts: { text: string; highlight: boolean }[] = [];
  const lowerLabel = label.toLowerCase();
  const lowerQuery = query.toLowerCase();
  let lastIndex = 0;

  let index = lowerLabel.indexOf(lowerQuery);
  while (index !== -1) {
    if (index > lastIndex) {
      parts.push({ text: label.slice(lastIndex, index), highlight: false });
    }
    parts.push({
      text: label.slice(index, index + query.length),
      highlight: true,
    });
    lastIndex = index + query.length;
    index = lowerLabel.indexOf(lowerQuery, lastIndex);
  }

  if (lastIndex < label.length) {
    parts.push({ text: label.slice(lastIndex), highlight: false });
  }

  return parts;
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;

  & > * {
    &:not(:last-child) {
      margin-right: $spacing * 0.5;
    }
  }
}

.beforeText {
  width: 20px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  margin-left: -4px;
}

.label {
  width: 100%;
  min-width: 0; // Needed for correct "oneLine" width

  .base:not(.breakLines) & {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .base:not(.selected) & {
    // Pretty hacky, but this fixes an issue in Safari where it applies the ellipsis incorrectly when the list has a scrollbar.
    // This basically makes the content wider, which gets rid of the ellipsis.
    // Will add extra "padding" on the right side, but that's ok.
    &:after {
      content: '';
      display: inline-block;
      width: 8px;
    }
  }
}

.label.hasBadgeLabel {
  width: auto;
}

.base .badgeLabel {
  flex-shrink: 0;
  margin-right: auto;
}

.labelExtra {
  margin-left: $spacing;
  color: var(--text-secondary);
  white-space: nowrap;
}

.highlight {
  border-radius: 2px;
  background-color: var(--action-primary-softer);
}

.actions {
  padding: $spacing * 0.5;
  margin: $spacing * -0.5 $spacing * -1 $spacing * -0.5 $spacing * -0.5;
}

.warnings {
  display: flex;
  align-items: center;

  .base:not(.selected) & {
    margin-right: $spacing * -0.5;
  }

  & > * {
    margin-left: $spacing * 0.25;
  }
}
</style>
